import { Component } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ModalController, NavParams } from '@ionic/angular';
import { EnumGenericRoleActionAlerts } from '@pages/BasePage';
import { IConsumerCardSearchFilterModel, IConsumerCardSearchModel } from '@models/Consumers';
import { FormValidators } from '@validators/form.validators';
import { StringUtils } from '@utils/StringUtils';

/**
 * Campo pelo qual se pesquisa o cartão. Só um está activo de cada vez.
 */
export enum EnumConsumerCardSearchField {
    VatNumber = 1,
    CardNumber = 2
}

@Component({
    selector: 'app-search-consumer-card',
    templateUrl: './search-consumer-card.component.html',
    styleUrls: ['./search-consumer-card.component.scss'],
})
export class SearchConsumerCardModalComponent {

    public Title: string = '';

    public Form: FormGroup;

    public EnumConsumerCardSearchField = EnumConsumerCardSearchField;

    public Data: IConsumerCardSearchModel[] = [];

    public SelectedId: string = null;

    public HasSearched: boolean = false;

    private onSearch: Function;
    private showToast: Function;

    /** consumidor cujo perfil está aberto; não faz sentido associá-lo a si próprio */
    private currentConsumer: IConsumerCardSearchFilterModel = {};

    constructor(
        private NavParams: NavParams,
        private ModalController: ModalController) {

        this.Title = this.NavParams.get('title') || 'Pesquisa de cartão';
        this.onSearch = this.NavParams.get('onSearch') || null;
        this.showToast = this.NavParams.get('showToast') || null;
        this.currentConsumer = this.NavParams.get('currentConsumer') || {};

        this.Form = new FormGroup({
            SearchType: new FormControl(EnumConsumerCardSearchField.VatNumber),
            VatNumber: new FormControl(null, [FormValidators.NIFValidator]),
            CardNumber: new FormControl(null, [FormValidators.NumberValidator])
        });
    }

    /**
     * Nome do controlo do campo activo.
     */
    private get activeControlName(): 'VatNumber' | 'CardNumber' {

        return this.Form.get('SearchType')?.value == EnumConsumerCardSearchField.CardNumber ? 'CardNumber' : 'VatNumber';
    }

    /**
     * Texto do campo activo, para as mensagens.
     */
    private get activeFieldName(): string {

        return this.activeControlName == 'CardNumber' ? 'o nº do cartão' : 'o NIF';
    }

    /** descrição do estado vazio, antes da primeira pesquisa */
    public get EmptyDescription(): string {

        return `Indique ${this.activeFieldName}`;
    }

    public IsSearchType(type: EnumConsumerCardSearchField): boolean {

        return this.Form.get('SearchType')?.value == type;
    }

    /**
     * Troca o campo de pesquisa. O valor escrito no anterior não se aproveita —
     * um NIF não é um nº de cartão — por isso limpa-se tudo, tal como os resultados.
     * @param ev
     * @param type
     */
    public OnChangeSearchType(ev: any, type: EnumConsumerCardSearchField) {

        ev?.preventDefault();
        ev?.stopPropagation();

        if (this.IsSearchType(type)) return;

        this.Form.reset({ SearchType: type });

        this.OnClearResults();
    }

    /**
     * Só o campo activo conta.
     */
    public get HasFilter(): boolean {

        return !!this.filterValue(this.activeControlName);
    }

    /**
     *
     * @param ev
     * @returns
     */
    public async OnSearch(ev?: any) {

        ev?.preventDefault();
        ev?.stopPropagation();

        this.Form.markAllAsTouched();

        const controlName = this.activeControlName;

        if (!this.HasFilter) {

            if (this.showToast)
                this.showToast(`Indique ${this.activeFieldName}`);

            return;
        }

        if (this.Form.get(controlName).invalid) {

            if (this.showToast)
                this.showToast(controlName == 'CardNumber'
                    ? 'O nº de cartão introduzido não está válido'
                    : 'O NIF introduzido não está válido');

            return;
        }

        // só vai o campo escolhido; o outro nem sequer está preenchido
        const filters: IConsumerCardSearchFilterModel =
        {
            VatNumber: controlName == 'VatNumber' ? this.filterValue('VatNumber') : null,
            CardNumber: controlName == 'CardNumber' ? this.filterValue('CardNumber') : null
        };

        if (this.filterValue(controlName) == this.currentConsumerValue(controlName)) {

            if (this.showToast)
                this.showToast(controlName == 'CardNumber'
                    ? 'O nº de cartão indicado é o do próprio consumidor'
                    : 'O NIF indicado é o do próprio consumidor');

            return;
        }

        if (!this.onSearch) return;

        this.Data = await this.onSearch(filters) || [];

        this.SelectedId = null;

        this.HasSearched = true;
    }

    /**
     * Limpa os campos, os resultados e a selecção.
     * @param ev
     */
    public OnClear(ev?: any) {

        ev?.preventDefault();
        ev?.stopPropagation();

        // limpar não muda o campo escolhido
        this.Form.reset({ SearchType: this.Form.get('SearchType')?.value });

        this.OnClearResults();
    }

    /**
     * Limpa apenas os resultados. O botão de limpar da searchbar já limpa o
     * respectivo campo, e limpar um campo não deve apagar o outro.
     */
    public OnClearResults() {

        this.Data = [];
        this.SelectedId = null;
        this.HasSearched = false;
    }

    /**
     * Selecção única, com desselecção ao reclicar na mesma linha.
     * @param item
     */
    public OnSelect(item: IConsumerCardSearchModel) {

        this.SelectedId = this.SelectedId == item?.Id ? null : item?.Id;
    }

    public IsSelected(item: IConsumerCardSearchModel): boolean {

        return !!item?.Id && this.SelectedId == item?.Id;
    }

    /**
     * Número do cartão, inteiro e em blocos de 4.
     * @param number
     * @returns
     */
    public CardNumber(number: string): string {

        return number ? StringUtils.FormatToCreditCardNumber(number) : '--';
    }

    /**
     *
     * @param ev
     * @returns
     */
    public OnConfirm(ev?: any) {

        ev?.preventDefault();
        ev?.stopPropagation();

        const selected = this.Data.find(it => it?.Id == this.SelectedId) || null;

        if (!selected) {

            if (this.showToast)
                this.showToast('Selecione um cartão');

            return;
        }

        this.ModalController.dismiss(selected, EnumGenericRoleActionAlerts.Save);
    }

    public Close() {

        this.ModalController.dismiss(null, EnumGenericRoleActionAlerts.Close);
    }

    private filterValue(controlName: string): string {

        return StringUtils.RemoveWhiteSpace(this.Form.get(controlName)?.value || '') || null;
    }

    /**
     * O nº de cartão do consumidor vem formatado com espaços, por isso compara-se sempre
     * sem espaços, à semelhança do que se faz aos valores introduzidos.
     * @param field
     * @returns
     */
    private currentConsumerValue(field: 'VatNumber' | 'CardNumber'): string {

        return StringUtils.RemoveWhiteSpace(this.currentConsumer?.[field] || '') || null;
    }
}
